import React, { useRef, useState, useEffect } from 'react';
import uniqBy from 'lodash/uniqBy';
import { connect } from 'react-redux';
import { storage } from 'firebase/fbConfig.js';

// react component for creating dynamic tables
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import FavoriteIcon from '@material-ui/icons/Favorite';

// core components
import Carousel from './SwipeCarousel.jsx';
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';
import LoaderWhite from 'components/LoaderWhite';
import MessageInfo from 'components/MessageInfo';
import Card from 'components/Card/Card.jsx';
import SwipableTracksControls from 'components/SwipableTracksControls';
import Store from 'services/StoreService';
import Loader from 'components/Loader';
import { updateUserData } from 'store/actions/userActions';
import config from 'config';

const Swipe = ({
  uid,
  fetching,
  collection,
  onListEnd,
  initialSlide,
  isUserList,
  recentActivity,
  favoriteSongs,
  updateUserData,
}) => {
  const playerRef = useRef(null);
  const carouselRef = useRef(null);

  const [index, setIndex] = useState(initialSlide);
  const [swiping, setSwiping] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioData, setAudioData] = useState(null);
  const [audioEnded, setAudioEnded] = useState(false);

  useEffect(() => {
    if (collection) {
      setAudioData(collection[index]);
    }
  }, [collection]);

  useEffect(() => {
    if (audioData) {
      getAudioFile();
    }
  }, [audioData]);

  const pauseAudio = () => {
    playerRef.current.audio.current.pause();
  };

  const playAudio = () => {
    playerRef.current.audio.current.play();
    setAudioEnded(false);
  };

  const previousSong = () => {
    carouselRef.current.slickPrev();
  };

  const nextSong = () => {
    carouselRef.current.slickNext();
  };

  const onNextSong = (ind) => {
    setIndex(ind);
    if (!isUserList && ind === collection.length - 1) {
      return onListEnd();
    }
    setAudioData(collection[ind]);
    setAudioEnded(false);
  };

  const onAudioEnded = () => {
    setAudioEnded(true);
    nextSong();
  };

  const approve = () => {
    nextSong();
    toggleIsFavorite();
  };

  const getAudioFile = async () => {
    try {
      const { file, audioURL, preview_url } = audioData;
      if (file) return setAudioFile(file);
      if (preview_url) return setAudioFile(preview_url);
      const storageRef = storage.refFromURL(audioURL);
      const url = await storageRef.getDownloadURL();
      setAudioFile(url);
    } catch (error) {
      // _TODO: error handling
      console.log(error);
    }
  };

  const toggleIsFavorite = async () => {
    try {
      const list = inFavorites
        ? //Remove from favorites if was already in favorites
          favoriteSongs.filter((e) => e.id !== audioData.id) : 
          //Add to favorites if was not already in favorites
          uniqBy(favoriteSongs.concat(audioData), 'id');
      // Update db
      await Store.updateUserFavorites(uid, list);
      // Before updating app state
      updateUserData({ favoriteSongs: list });
    } catch (error) {
      console.error(error);
    }
  };

  const addToRecentActivity = async () => {
    try {
      let list = [...(recentActivity || [])];
      list.unshift(audioData);
      list = uniqBy(list, 'id');
      if (list.length > config.nbOfRecentActivity) {
        list.pop();
      }
      // Update db
      await Store.updateUserActivity(uid, list);
      // Before updating app state
      updateUserData({ recentActivity: list });
    } catch (error) {
      console.error(error);
    }
  };

  if (!collection || !uid) {
    return <LoaderWhite />;
  }

  if (!collection.length) {
    return <MessageInfo color='white' text='No tracks found' />;
  }

  const inFavorites =
    audioData &&
    !swiping &&
    favoriteSongs.map((e) => e.id).includes(audioData.id);

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <GridContainer>
            <GridItem
              style={{
                margin: '0px -30px 0px -30px',
                width: '117%',
              }}
            >
              <Card
                profile
                style={{
                  background: '#EEEFFD',
                  borderRadius: '20px 20px 0px 0px',
                }}
              >
                <GridContainer>
                  <GridItem xs={12}>
                    <div
                      style={{
                        margin: '25px',
                        borderRadius: '20px',
                        boxShadow: '0 0 10px #bbb',
                        backgroundColor: '#FFFFFF',
                        minHeight: '480px',
                        position:'relative'
                      }}
                    >
                      { fetching && (
                          <div style={{ 
                            color: '#757575',
                            fontSize: '20px',
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            height: '480px',
                            backgroundColor: 'rgba(255,255,255,0.5)',
                            position: 'absolute',
                            width: '100%',
                            zIndex: '1000',
                          }}>
                            <div style={{ padding: '20px 30px 10px 30px', borderRadius: '50px', background: 'white' }}>
                              Loading more tracks
                              <Loader />
                            </div>
                          </div>
                      )}
                      <Carousel
                        ref={carouselRef}
                        data={collection.slice(0, 100)}
                        onPlay={playAudio}
                        onNext={onNextSong}
                        onPause={pauseAudio}
                        initialSlide={parseInt(index, 10)}
                        audioEnded={audioEnded}
                        afterChangeCb={() => setSwiping(false)}
                        beforeChangeCb={() => setSwiping(true)}
                      />
                    </div>
                  </GridItem>
                </GridContainer>
                <GridContainer
                  style={{ marginTop: '20px', paddingBottom: '50px' }}
                >
                  {!fetching && ( collection.length > 1 ? (
                    <SwipableTracksControls
                      approve={approve}
                      nextSong={nextSong}
                      canGoBack={isUserList || index > 0}
                      inFavorites={inFavorites}
                      previousSong={previousSong}
                      toggleIsFavorite={toggleIsFavorite}
                    />
                  ) : (
                    <GridItem xs={12}>
                      <FavoriteIcon
                        style={{
                          color: inFavorites ? 'red' : 'black',
                          fontSize: '50px',
                        }}
                        onClick={toggleIsFavorite}
                      />
                    </GridItem>
                  ))}
                </GridContainer>
              </Card>
            </GridItem>
          </GridContainer>
          {audioData && (
          <AudioPlayer
            ref={playerRef}
            src={audioFile}
            style={{ position: 'absolute', top: '-1000px' }}
            onPlay={addToRecentActivity}
            autoPlay={true}
            showJumpControls={false}
            showFilledProgress={false}
            showDownloadProgress={false}
            onEnded={onAudioEnded}
            // TODO: remove
            volume="0.1"
          />
    )}
        </GridItem>
      </GridContainer>
    </div>
  );
};

Swipe.defaultProps = {
  onListEnd: () => null,
  favoriteSongs: [],
};

const mapStateToProps = (state, ownProps) => {
  const { uid } = state.user || {};
  const { favoriteSongs, recentActivity } = state.user || {};
  return { uid, favoriteSongs, recentActivity };
};

export default connect(mapStateToProps, { updateUserData })(Swipe);
